import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  fetchWithAuth,
  createSlug,
  fetchWithAuthUpload,
} from "../../../Context/UserContext";
import { useCompany } from "../../../Context/CompanyContext";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import PageLayout from "../../../components/layout/PageLayout";
import { LoadingOverlay } from "../../../components/ui/loading-overlay";
import API_URL from "../../../config";
import PostulacionesDesktop from "./PostulacionesDesktop";
import PostulacionesMobile from "./PostulacionesMobile";
import ModalOverlay from "./PostulacionesModalOverlay"; // Importa el modal
import { filterPostulaciones, sortPostulaciones } from "./postulacionesUtils";
import { handleDownloadExcel } from "./PostulacionesDescarga";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Star, Download } from "lucide-react";

const Postulaciones = () => {
  const { tender_id: tenderId } = useParams();
  const { companies } = useCompany();
  const [tenderInfo, setTenderInfo] = useState(null);
  const [postulaciones, setPostulaciones] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false); // Estado para mostrar el popup
  const [confirmationPostulacion, setConfirmationPostulacion] = useState(null); // Estado para confirmar la postulación
  const navigate = useNavigate();
  const [filterConfig, setFilterConfig] = useState({});
  const [sortConfig, setSortConfig] = useState([]);
  const [columnas, setColumnas] = useState(1);
  const [filterQuestion, setFilterQuestion] = useState(null); // La pregunta activa para el modal
  const [isModalOpen, setIsModalOpen] = useState(false); // Estado para abrir/cerrar el modal
  const [modalFilterConfig, setModalFilterConfig] = useState(null); // Configuración temporal del filtro en el modal

  // Estado para mostrar el popup
  useEffect(() => {
    if (tenderId) {
      fetchTenderInfo(tenderId);
      fetchPostulaciones(tenderId);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchTenderInfo = async (id) => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(`${API_URL}/licit/tender/${id}`);
      if (response.ok) {
        const data = await response.json();
        setTenderInfo(data);
      } else if (response.status === 403) {
        toast.error("No tienes permisos para ver esta licitación.");
        navigate("/"); // 🔹 Redirige al usuario a la página principal o dashboard
      } else {
        console.error("Error al obtener la información de la licitación.");
      }
    } catch (error) {
      console.error("Error al obtener la información de la licitación:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPostulaciones = async (id) => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/licit/tender/${id}/postulaciones`
      );
      if (response.ok) {
        const data = await response.json();

        // Transformar las respuestas para incluir archivos adjuntos
        const postulacionesProcesadas = data.postulaciones.map(
          (postulacion) => {
            let respuestasProcesadas = {
              ...postulacion.respuestas_preguntas_licitador,
            };

            // Si la respuesta es "", asignamos el archivo correspondiente
            Object.entries(postulacion.archivos_respuestas || {}).forEach(
              ([idPregunta, archivo]) => {
                if (respuestasProcesadas[idPregunta] === "") {
                  respuestasProcesadas[idPregunta] = {
                    file_name: archivo.file_name,
                    file_path: archivo.file_path,
                    id_file: archivo.id_file,
                  };
                }
              }
            );

            return {
              ...postulacion,
              respuestas_preguntas_licitador: respuestasProcesadas,
            };
          }
        );

        setPostulaciones({ postulaciones: postulacionesProcesadas });
      } else {
        console.error("Error al obtener las postulaciones");
      }
    } catch (error) {
      console.error("Error al obtener las postulaciones:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadFile = async (fileId) => {
    setIsLoading(true);
    try {
      console.log(`⬇️ Solicitando descarga del archivo con ID: ${fileId}`);

      const response = await fetchWithAuthUpload(
        `${API_URL}/upload/download-file/${fileId}`,
        { method: "GET" }
      );

      if (!response.ok) {
        throw new Error("Error al obtener el archivo");
      }

      const data = await response.json();
      console.log("📡 Respuesta del backend:", data);

      const fileUrl = data.file_url; // ✅ URL presignada de S3
      console.log(`📂 Abriendo URL en nueva pestaña: ${fileUrl}`);

      // ✅ Abrir la URL presignada directamente (deja el UUID en el nombre)
      window.open(fileUrl, "_blank");
    } catch (error) {
      console.error("❌ Error al descargar archivo:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmWinner = async (applicationId) => {
    if (!confirmationPostulacion) return;
    setIsLoading(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/licit/tender/${tenderId}/select-winner`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ applicationId }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        navigate(`/admin-contrato/${data.contractId}`);
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.message}`);
      }
    } catch (error) {
      toast.error("Ocurrió un error al seleccionar al ganador.");
    } finally {
      setShowPopup(false); // Cerrar el popup
      setConfirmationPostulacion(null);
      setIsLoading(false);
    }
  };

  // Cerrar el modal
  const closeFilterModal = () => {
    setIsModalOpen(false);
    setFilterQuestion(null);
    setModalFilterConfig(null);
  };

  // Aplicar el filtro desde el modal
  const applyFilter = () => {
    setFilterConfig((prev) => ({
      ...prev,
      [filterQuestion.question]: modalFilterConfig,
    }));
    closeFilterModal();
  };

  // Limpiar el filtro
  const clearFilter = () => {
    setFilterConfig((prev) => {
      const updated = { ...prev };
      delete updated[filterQuestion.question];
      return updated;
    });
    closeFilterModal();
  };

  const sortedPostulaciones = React.useMemo(() => {
    // ✅ Verificar si postulaciones existe y tiene el array dentro
    if (
      !postulaciones ||
      !Array.isArray(postulaciones.postulaciones) ||
      postulaciones.postulaciones.length === 0
    ) {
      console.log("⚠️ No hay postulaciones, devolviendo array vacío");
      return [];
    }

    let filtered = filterPostulaciones(
      postulaciones.postulaciones,
      filterConfig
    );
    let sorted = sortPostulaciones(filtered, sortConfig);

    return sorted;
  }, [postulaciones, filterConfig, sortConfig]); // ✅ Se recalcula cuando estos valores cambian

  useEffect(() => {
    const newColumnas =
      Object.keys(filterConfig).length > 0
        ? sortedPostulaciones.length || 1
        : postulaciones?.postulaciones?.length || 1;

    setColumnas(newColumnas);
  }, [filterConfig, sortedPostulaciones, postulaciones]);

  const confirmSelectWinner = (postulacion) => {
    setConfirmationPostulacion(postulacion);
    setShowPopup(true); // Mostrar el popup
  };

  const handleSort = (question, type) => {
    const normalizedType =
      type === "Solo permite números"
        ? "number"
        : type === "Respuesta de Sí o No"
        ? "yesno"
        : type === "Solo archivos adjuntos"
        ? "adjuntos"
        : "open"; // Mapeo de tipos

    if (normalizedType === "open") return; // No ordenar preguntas abiertas

    setSortConfig((prev) => {
      const existing = prev.find((config) => config.question === question);
      const newOrder = existing
        ? existing.order === "asc"
          ? "desc"
          : existing.order === "desc"
          ? "none"
          : "asc"
        : "asc"; // Ciclo: asc -> desc -> none -> asc

      // Si el nuevo estado es "none", eliminamos la pregunta del orden
      if (newOrder === "none") {
        return prev.filter((config) => config.question !== question);
      }

      // Aseguramos que solo haya una pregunta ordenada a la vez
      return [{ question, order: newOrder, type: normalizedType }];
    });
  };

  // Abrir el modal con la pregunta y el tipo
  const openFilterModal = (question, type) => {
    setFilterQuestion({ question, type });
    setModalFilterConfig(filterConfig[question] || { min: "", max: "" }); // Configuración inicial
    setIsModalOpen(true);
  };

  // Parsear preguntas_licitador
  let preguntas = [];
  try {
    preguntas = Array.isArray(tenderInfo?.tender.preguntas_licitador)
      ? tenderInfo.tender.preguntas_licitador
      : JSON.parse(tenderInfo?.tender.preguntas_licitador || "[]");
  } catch (error) {
    console.error("Error al parsear preguntas_licitador:", error);
  }

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString); // Convierte la cadena en un objeto Date
      return format(date, "PPPP p", { locale: es }); // Formatea la fecha
    } catch {
      return "Fecha inválida"; // Maneja errores si el formato no es válido
    }
  };

  const currentUserId = parseInt(localStorage.getItem("current_user_id"));
  // Licitación de empresa: gobierna el rol actual (no basta con haberla creado).
  // Licitación personal (sin empresa): solo su creador puede aprobar/decidir.
  const canApprove = tenderInfo?.tender?.id_company
    ? companies?.company?.id_company === tenderInfo.tender.id_company &&
      ["Administrador", "Aprobador"].includes(companies?.company?.tipo_cuenta)
    : tenderInfo?.tender?.id_user === currentUserId;

  let minValue = 0; // Valor predeterminado
  let maxValue = 100; // Valor predeterminado

  if (filterQuestion && postulaciones?.postulaciones?.length > 0) {
    const valores = postulaciones.postulaciones.map((p) =>
      parseFloat(p.respuestas_preguntas_licitador[filterQuestion.question] || 0)
    );

    if (valores.length > 0) {
      minValue = Math.min(...valores);
      maxValue = Math.max(...valores);
    }
  }

  return (
    <PageLayout>
      {isLoading || !tenderInfo ? (
        <LoadingOverlay />
      ) : (
        <div className="mx-auto max-w-6xl space-y-2 px-4 pt-8 sm:px-6">
          <h2 className="text-xl font-semibold text-foreground">
            <Link
              to={`/ver-licitacion/${tenderInfo.tender.id_tender}/${createSlug(tenderInfo.tender.titulo)}`}
              className="hover:text-primary"
            >
              {tenderInfo.tender.titulo}
            </Link>
          </h2>
          <p className="text-sm text-muted-foreground">
            Escoge un postulante como ganador para calificarlo y gestionar su contrato.
          </p>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Nº de Licitación:</strong> {tenderInfo.tender.id_tender}
          </p>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Categoría:</strong> {tenderInfo.tender.categoria}
          </p>

          {tenderInfo.tender.company_name && (
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Empresa:</strong>{" "}
              <a
                href={`/company-profile/${tenderInfo.tender.id_company}/${encodeURIComponent(tenderInfo.tender.company_name)}`}
                className="text-primary hover:underline"
              >
                {tenderInfo.tender.company_name.length > 25
                  ? `${tenderInfo.tender.company_name.slice(0, 25)}...`
                  : tenderInfo.tender.company_name}
              </a>
            </p>
          )}

          {tenderInfo.tender.user_name && tenderInfo.tender.user_lastname && (
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Creador:</strong>{" "}
              <a
                href={`/profile/${tenderInfo.tender.id_user}/${encodeURIComponent(
                  `${tenderInfo.tender.user_name} ${tenderInfo.tender.user_lastname}`
                )}`}
                className="text-primary hover:underline"
              >
                {`${tenderInfo.tender.user_name} ${tenderInfo.tender.user_lastname}`.length > 20
                  ? `${`${tenderInfo.tender.user_name} ${tenderInfo.tender.user_lastname}`.slice(0, 20)}...`
                  : `${tenderInfo.tender.user_name} ${tenderInfo.tender.user_lastname}`}
              </a>
            </p>
          )}

          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Ciudades:</strong>{" "}
            {(() => {
              try {
                const ciudades = JSON.parse(tenderInfo.tender.ciudad);
                if (Array.isArray(ciudades)) {
                  const visibleCiudades = ciudades.slice(0, 4); // Limitar a 4 ciudades
                  return ciudades.length > 4
                    ? `${visibleCiudades.join(", ")}...` // Mostrar solo las primeras 4 con "..." si hay más
                    : visibleCiudades.join(", ");
                }
                return "No especificado";
              } catch (e) {
                return "No especificado";
              }
            })()}
          </p>

          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Actualizado:</strong> {formatDate(tenderInfo.tender.created_at)}
          </p>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Fecha max:</strong>{" "}
            {formatDate(tenderInfo.tender.fecha_maxima_postulacion)}
          </p>
          <div className="flex flex-wrap gap-2 pt-2">
            {tenderInfo.tender.estado === "licitacion-ganada" && (
              <Button variant="outline" onClick={() => navigate(`/admin-contrato/${tenderInfo.tender.ganador}`)}>
                <Star className="h-4 w-4" /> Gestionar al Ganador
              </Button>
            )}

            <Button
              variant="outline"
              onClick={() => handleDownloadExcel(tenderInfo, sortedPostulaciones, preguntas)}
            >
              <Download className="h-4 w-4" /> Descargar Excel
            </Button>
          </div>
        </div>
      )}
      <ModalOverlay
        isOpen={isModalOpen}
        filterQuestion={filterQuestion}
        modalFilterConfig={modalFilterConfig}
        minValue={minValue}
        maxValue={maxValue}
        setModalFilterConfig={setModalFilterConfig}
        applyFilter={applyFilter} // Puedes definir una función real aquí
        clearFilter={clearFilter} // Puedes definir una función real aquí
        closeFilterModal={() => setIsModalOpen(false)} // ✅ Esto cerrará el modal
      />

      <div className="mx-auto max-w-6xl px-4 pb-8 sm:px-6">
        {isMobile ? (
          <PostulacionesMobile
            sortedPostulaciones={sortedPostulaciones}
            tenderInfo={tenderInfo}
            downloadFile={downloadFile}
            confirmSelectWinner={canApprove ? confirmSelectWinner : null}
          />
        ) : (
          <PostulacionesDesktop
            sortedPostulaciones={sortedPostulaciones}
            tenderInfo={tenderInfo}
            columnas={columnas}
            downloadFile={downloadFile}
            confirmSelectWinner={canApprove ? confirmSelectWinner : null}
            preguntas={preguntas}
            filterConfig={filterConfig}
            setFilterConfig={setFilterConfig}
            sortConfig={sortConfig}
            handleSort={handleSort}
            openFilterModal={openFilterModal}
            postulaciones={postulaciones}
          />
        )}
      </div>
      <Dialog open={showPopup && !!confirmationPostulacion} onOpenChange={(open) => !open && setShowPopup(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar ganador</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            ¿Estás seguro de seleccionar a{" "}
            {confirmationPostulacion?.company_postulante?.nombre_empresa || "este postulante"} como
            ganador?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPopup(false)}>
              Cancelar
            </Button>
            <Button onClick={() => handleConfirmWinner(confirmationPostulacion.id_application)}>
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default Postulaciones;
